import {
	DocumentData,
	collection,
	doc,
	getDoc,
	getDocs,
	query,
	where,
} from "firebase/firestore";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { User } from "../../models/User";
import { db } from "../../firebase";
import { DashboardAthlete, DashboardRegistration } from "./dashboardHelpers";

export type AdultLeagueMembershipSummary = DocumentData & {
	id: string;
	active?: boolean;
	endDate?: string;
	eventID?: string;
	eventTitle?: string;
	location?: string;
	nextMatchAt?: string;
	paidAt?: Date | { toDate: () => Date } | string | null;
	role?: "player" | "free_agent";
	startDate?: string;
	teamID?: string;
	teamName?: string;
};

const formatLocationDisplay = (
	event: DocumentData,
	locationMap: Record<string, DocumentData>,
) => {
	const location = event.locationID ? locationMap[event.locationID] : null;
	if (!location) {
		return event.location ?? "";
	}
	return [location.name, location.address].filter(Boolean).join(" · ");
};

const normalizeDashboardTeam = (team: DocumentData) => ({
	id: team.id,
	name: team.name,
	inviteID: team.inviteID,
	roster: team.roster ?? [],
	paidAt: team.paidAt ?? null,
	paymentRegistrationID: team.paymentRegistrationID,
	freeAgentInvites: team.freeAgentInvites ?? [],
	rosterPublished: team.rosterPublished,
	dues: team.dues,
});

const normalizeDashboardEvent = (
	event: DocumentData,
	locationMap: Record<string, DocumentData>,
) => {
	const normalizedEvent: DocumentData = {
		id: event.id,
		active: event.active,
		cost: event.cost,
		date: event.date,
		description: event.description,
		endDate: event.endDate,
		endTime: event.endTime,
		hidden: event.hidden,
		hatchlingsMatchDays: event.hatchlingsMatchDays,
		hatchlingsPracticeDays: event.hatchlingsPracticeDays,
		hatchlingsPracticeStartTime: event.hatchlingsPracticeStartTime,
		leadCoachName: event.leadCoachName,
			leagueDayOfWeek: event.leagueDayOfWeek,
			leagueType: event.leagueType,
			leagueMatchTimes: event.leagueMatchTimes,
			leaguePlayDay: event.leaguePlayDay,
		location: formatLocationDisplay(event, locationMap),
		locationID: event.locationID,
		manualSoldOutSessions: event.manualSoldOutSessions,
		maxAge: event.maxAge,
		maximumRegistrations: event.maximumRegistrations,
		maxTeams: event.maxTeams,
		minAge: event.minAge,
		minPlayers: event.minPlayers,
		multipleSessions: event.multipleSessions,
		open: event.open,
		openString: event.openString,
		paidRegs: event.paidRegs,
		registrationType: event.registrationType,
		season: event.season,
		sessionPaidArr: event.sessionPaidArr,
		sessions: event.sessions,
		soldOut: event.soldOut,
		startDate: event.startDate,
		startTime: event.startTime,
		subType: event.subType,
		title: event.title,
		tryoutSeason: event.tryoutSeason,
		type: event.type,
	};

	if (event.type === "League") {
		normalizedEvent.freeAgents = event.freeAgents ?? [];
		normalizedEvent.leagueWeeks = event.leagueWeeks ?? [];
		normalizedEvent.teams = (event.teams ?? []).map(normalizeDashboardTeam);
	}

	if (event.type === "Tryout") {
		normalizedEvent.teams = (event.teams ?? []).map(normalizeDashboardTeam);
		normalizedEvent.tournaments = event.tournaments ?? [];
	}

	return normalizedEvent;
};

const sortDashboardEvents = (events: DocumentData[]) =>
	[...events].sort((a, b) =>
		a.date === b.date
			? a.title > b.title
				? -1
				: 1
			: a.date > b.date
				? 1
				: -1,
	);

const getEventIDsFromAthletes = (athletes: DashboardAthlete[]) =>
	athletes.flatMap((athlete) =>
		(athlete.registrations ?? []).map((registration) => registration.id),
	);

const getAdultLeagueMemberships = async (userID: string) => {
	const membershipSnapshot = await getDocs(
		collection(db, "users", userID, "adultLeagueMemberships"),
	);

	return membershipSnapshot.docs
		.map((membershipDoc) => ({
			...membershipDoc.data(),
			id: membershipDoc.id,
		}) as AdultLeagueMembershipSummary)
		.filter((membership) => membership.active !== false)
};

const getLocationMapForEvents = async (rawEvents: DocumentData[]) => {
	const locationIDs = Array.from(
		new Set(
			rawEvents
				.map((event) => event.locationID)
				.filter((locationID): locationID is string => Boolean(locationID)),
		),
	);
	if (locationIDs.length === 0) {
		return {};
	}
	const locationIDSet = new Set(locationIDs);

	const locationSnapshot = await getDocs(collection(db, "practiceLocations"));
	const locationEntries = locationSnapshot.docs
		.filter((locationDoc) => locationIDSet.has(locationDoc.id))
		.map(
			(locationDoc): [string, DocumentData] => [
				locationDoc.id,
				{
					...locationDoc.data(),
					id: locationDoc.id,
				} as DocumentData,
			],
		);

	return Object.fromEntries(locationEntries);
};

const normalizeEventsWithLocations = async (
	rawEvents: DocumentData[],
	setLoadingStage?: (stage: string) => void,
	options: { loadLocations?: boolean } = {},
) => {
	const { loadLocations = true } = options;
	if (!loadLocations) {
		return sortDashboardEvents(
			rawEvents.map((event) => normalizeDashboardEvent(event, {})),
		);
	}

	const locationCount = new Set(
		rawEvents
			.map((event) => event.locationID)
			.filter((locationID): locationID is string => Boolean(locationID)),
	).size;
	if (locationCount > 0) {
		setLoadingStage?.(
			`Loading event locations (${locationCount} ${
				locationCount === 1 ? "location" : "locations"
			})...`,
		);
	}
	const locationMap = await getLocationMapForEvents(rawEvents);
	return sortDashboardEvents(
		rawEvents.map((event) => normalizeDashboardEvent(event, locationMap)),
	);
};

const getEventsByID = async (
	eventIDs: string[],
	setLoadingStage?: (stage: string) => void,
	options: { loadLocations?: boolean } = {},
) => {
	const uniqueEventIDs = Array.from(new Set(eventIDs.filter(Boolean)));
	if (uniqueEventIDs.length === 0) {
		return [];
	}

	setLoadingStage?.(
		`Loading registered event details (${uniqueEventIDs.length} ${
			uniqueEventIDs.length === 1 ? "event" : "events"
		})...`,
	);
	const eventEntries = await Promise.all(
		uniqueEventIDs.map(async (eventID) => {
			const eventSnap = await getDoc(doc(db, "events", eventID));
			return eventSnap.exists()
				? ({
						...eventSnap.data(),
						id: eventSnap.id,
					} as DocumentData)
				: null;
		}),
	);

	return normalizeEventsWithLocations(
		eventEntries.filter((event): event is DocumentData => event !== null),
		setLoadingStage,
		options,
	);
};

const mergeEvents = (
	currentEvents: DocumentData[],
	availableEvents: DocumentData[],
) => {
	const eventMap = new Map<string, DocumentData>();
	currentEvents.forEach((event) => eventMap.set(event.id, event));
	availableEvents.forEach((event) => eventMap.set(event.id, event));
	return sortDashboardEvents(Array.from(eventMap.values()));
};

const mergeEventList = (
	currentEvents: DocumentData[],
	nextEvents: DocumentData[],
) => {
	const eventMap = new Map<string, DocumentData>();
	currentEvents.forEach((event) => eventMap.set(event.id, event));
	nextEvents.forEach((event) => eventMap.set(event.id, event));
	return sortDashboardEvents(Array.from(eventMap.values()));
};

const getAvailableEventLoadKey = (eventType?: string | null) =>
	`${eventType ?? ""}`.trim() || "all";

const useDashboardData = (user: User) => {
	const [loading, setLoading] = useState(true);
	const [loadingStage, setLoadingStage] = useState("Starting dashboard...");
	const [loadError, setLoadError] = useState("");
	const [userData, setUserData] = useState<any>(null);
	const [adultLeagueMemberships, setAdultLeagueMemberships] = useState<
		AdultLeagueMembershipSummary[]
	>([]);
	const [athletes, setAthletes] = useState<DashboardAthlete[]>([]);
	const [currentEvents, setCurrentEvents] = useState<DocumentData[]>([]);
	const [availableEvents, setAvailableEvents] = useState<DocumentData[]>([]);
	const [availableEventErrors, setAvailableEventErrors] = useState<Record<string, string>>({});
	const [availableEventLoadKeys, setAvailableEventLoadKeys] = useState<string[]>([]);
	const [availableEventLoadingKeys, setAvailableEventLoadingKeys] = useState<string[]>([]);
	const availableEventLoadKeysRef = useRef<Set<string>>(new Set());
	const availableEventLoadingKeysRef = useRef<Set<string>>(new Set());
	const events = useMemo(
		() => mergeEvents(currentEvents, availableEvents),
		[currentEvents, availableEvents],
	);

	const fetchRegistrations = useCallback(
		async (allAths: DashboardAthlete[]) => {
			setLoadingStage("Loading family registrations...");
			const athletesByID = new Map(
				allAths.map((athlete) => [
					athlete.id,
					{
						...athlete,
						registrations: [] as DashboardRegistration[],
					},
				]),
			);
			const familyRegistrationQuery = query(
				collection(db, "registrations"),
				where("parentID", "==", user.id),
			);
			const querySnapshot = await getDocs(familyRegistrationQuery);

			querySnapshot.forEach((doc) => {
				const data = doc.data();
				const athlete = athletesByID.get(data.athlete);
				if (!athlete) {
					return;
				}

				athlete.registrations = [
					...(athlete.registrations ?? []),
					{
						id: data.event,
						regID: doc.id,
						status: data.status,
						paidAt: data.paidAt ? data.paidAt.toDate() : null,
						session: data.session,
						practiceTeam: data.isPracticeTeam ?? null,
					},
				];
			});

			const nextAthletes = Array.from(athletesByID.values());
			setAthletes(nextAthletes);
			return nextAthletes;
		},
		[user.id],
	);

	const fetchAthletes = useCallback(async () => {
		const colRef = collection(db, "users", user.id, "athletes");

		try {
			setLoadingStage("Loading athletes...");
			const querySnapshot = await getDocs(colRef);
			const tmpAths: DashboardAthlete[] = [];

			querySnapshot.forEach((doc) => {
				const tmpAth = {
					...doc.data(),
					id: doc.id,
					parentID: user.id,
				} as DashboardAthlete;
				tmpAths.push(tmpAth);
			});

			const nextAthletes = await fetchRegistrations(tmpAths);
			return nextAthletes;
		} catch (error) {
			setLoadError("Could not load your athletes. Please refresh and try again.");
			setLoading(false);
			return [];
		}
	}, [fetchRegistrations, user.id]);

	const fetchCurrentEvents = useCallback(
		async (
			nextAthletes: DashboardAthlete[],
		) => {
			const eventIDs = getEventIDsFromAthletes(nextAthletes);
			const nextEvents = await getEventsByID(eventIDs, setLoadingStage, {
				loadLocations: false,
			});
			setCurrentEvents(nextEvents);
			return nextEvents;
		},
		[],
	);

	const isAvailableEventsLoadedForType = useCallback(
		(eventType?: string | null) =>
			availableEventLoadKeys.includes("all") ||
			availableEventLoadKeys.includes(getAvailableEventLoadKey(eventType)),
		[availableEventLoadKeys],
	);
	const isAvailableEventsLoadingForType = useCallback(
		(eventType?: string | null) =>
			availableEventLoadingKeys.includes("all") ||
			availableEventLoadingKeys.includes(getAvailableEventLoadKey(eventType)),
		[availableEventLoadingKeys],
	);
	const getAvailableEventsErrorForType = useCallback(
		(eventType?: string | null) =>
			availableEventErrors[getAvailableEventLoadKey(eventType)] ?? "",
		[availableEventErrors],
	);

	const fetchAvailableEvents = useCallback(async (eventType?: string | null) => {
		const loadKey = getAvailableEventLoadKey(eventType);
		if (
			availableEventLoadKeysRef.current.has(loadKey) ||
			availableEventLoadingKeysRef.current.has(loadKey)
		) {
			return;
		}
		availableEventLoadingKeysRef.current.add(loadKey);
		setAvailableEventLoadingKeys((currentKeys) =>
			currentKeys.includes(loadKey) ? currentKeys : [...currentKeys, loadKey],
		);
		setAvailableEventErrors((currentErrors) => ({
			...currentErrors,
			[loadKey]: "",
		}));
		const eventTypeLabel = loadKey === "all" ? "registration" : eventType;
		setLoadingStage(`Loading available ${eventTypeLabel} events...`);
		const eventQuery =
			loadKey === "all"
				? query(collection(db, "events"), where("hidden", "==", false))
				: query(
						collection(db, "events"),
						where("hidden", "==", false),
						where("type", "==", eventType),
					);
		try {
			const eventSnapshot = await getDocs(eventQuery);
			const rawEvents: DocumentData[] = eventSnapshot.docs.map((eventDoc) => ({
				...eventDoc.data(),
				id: eventDoc.id,
			}));
			const normalizedEvents = await normalizeEventsWithLocations(
				rawEvents,
				setLoadingStage,
				{
					loadLocations: false,
				},
			);
			setAvailableEvents((currentEvents) =>
				loadKey === "all"
					? normalizedEvents
					: mergeEventList(currentEvents, normalizedEvents),
			);
			setAvailableEventLoadKeys((currentKeys) =>
				currentKeys.includes(loadKey) ? currentKeys : [...currentKeys, loadKey],
			);
			availableEventLoadKeysRef.current.add(loadKey);
			const shouldHydrateLocations =
				loadKey !== "League" && rawEvents.some((event) => Boolean(event.locationID));
			if (shouldHydrateLocations) {
				normalizeEventsWithLocations(rawEvents)
					.then((eventsWithLocations) => {
						setAvailableEvents((currentEvents) =>
							loadKey === "all"
								? eventsWithLocations
								: mergeEventList(currentEvents, eventsWithLocations),
						);
					})
					.catch((error) => {
						console.error("Error loading available event locations:", error);
					});
			}
		} catch (error) {
			console.error("Error loading available registration events:", error);
			setAvailableEventErrors((currentErrors) => ({
				...currentErrors,
				[loadKey]: "Available registrations could not be loaded. Please try again.",
			}));
		} finally {
			availableEventLoadingKeysRef.current.delete(loadKey);
			setAvailableEventLoadingKeys((currentKeys) =>
				currentKeys.filter((key) => key !== loadKey),
			);
		}
	}, []);

	const updateDashboardEvent = useCallback((nextEvent: DocumentData) => {
		setCurrentEvents((currentEvents) =>
			currentEvents.some((event) => event.id === nextEvent.id)
				? mergeEventList(currentEvents, [nextEvent])
				: currentEvents,
		);
		setAvailableEvents((currentEvents) =>
			currentEvents.some((event) => event.id === nextEvent.id)
				? mergeEventList(currentEvents, [nextEvent])
				: currentEvents,
		);
	}, []);

	const fetchData = useCallback(async () => {
		setLoadError("");
		setLoadingStage("Loading profile, athletes, registrations, and memberships...");

		try {
			const docRef = doc(db, "users", user.id);
			const [snap, nextAthletes, nextAdultLeagueMemberships] = await Promise.all([
				getDoc(docRef),
				fetchAthletes(),
				getAdultLeagueMemberships(user.id),
			]);
			if (!snap.exists()) {
				setLoading(false);
				setLoadError("Your profile could not be found. Please refresh and try again.");
				return;
			}

			setUserData(snap.data());
			setAdultLeagueMemberships(nextAdultLeagueMemberships);
			await fetchCurrentEvents(nextAthletes);
			setLoadingStage("Preparing dashboard...");
			setLoading(false);
		} catch (error) {
			setLoading(false);
			setLoadError("Could not load your dashboard. Please refresh and try again.");
		}
	}, [
		fetchAthletes,
		fetchCurrentEvents,
		user.id,
	]);

	useEffect(() => {
		fetchData();
		return;
	}, [fetchData]);

	const addEventRegistration = (
		eventID: string,
		athleteID: string,
		regID: string,
		status: string,
		practiceTeam: null | boolean,
		session: null | string,
	) => {
		const nextRegistration = {
			id: eventID,
			regID,
			status,
			paidAt: status === "paid" ? new Date() : null,
			practiceTeam,
			session,
		};
		setAthletes((currentAthletes) =>
			currentAthletes.map((athlete) => {
				if (athlete.id !== athleteID) {
					return athlete;
				}
				const registrations = athlete.registrations ?? [];
				if (registrations.some((registration) => registration.regID === regID)) {
					return {
						...athlete,
						registrations: registrations.map((registration) =>
							registration.regID === regID
								? {
										...registration,
										...nextRegistration,
									}
								: registration,
						),
					};
				}
				return {
					...athlete,
					registrations: [...registrations, nextRegistration],
				};
			}),
		);
	};

	const removeEventRegistration = (
		athleteID: string,
		regID: string,
	) => {
		setAthletes((currentAthletes) =>
			currentAthletes.map((athlete) =>
				athlete.id === athleteID
					? {
							...athlete,
							registrations: (athlete.registrations ?? []).filter(
								(registration) => registration.regID !== regID,
							),
						}
					: athlete,
			),
		);
	};

	return {
		addEventRegistration,
		adultLeagueMemberships,
		athletes,
		events,
		fetchAvailableEvents,
		fetchAthletes,
		fetchData,
		getAvailableEventsErrorForType,
		isAvailableEventsLoadedForType,
		isAvailableEventsLoadingForType,
		loadError,
		loading,
		loadingStage,
		removeEventRegistration,
		updateDashboardEvent,
		userData,
	};
};

export default useDashboardData;
